import moment from 'moment';
import { useCallback, useMemo } from 'react';

import { CommonProposalFieldProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/types';
import { useScheduleEmployments } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useScheduleEmployments';
import { getFirstInArray, getLastInArray, sortDates } from '~/utils/helper';

type DateLimitingProps = {
  value?: Date | null;
  minDate?: Date;
  maxDate?: Date;
};

type PropsType = {
  startDate: DateLimitingProps;
  endDate: DateLimitingProps;
  employmentId?: string | number;
};

export const useCommonFieldProps = (
  props: PropsType,
): CommonProposalFieldProps & {
  isLoading: boolean;
} => {
  const { startDate, endDate, employmentId } = props;

  const { getEmploymentDateRange, isLoading } = useScheduleEmployments();

  const resolveDateFieldLimits = useCallback(
    (
      employmentId?: number | string,
      startDate?: DateLimitingProps,
      endDate?: DateLimitingProps,
    ) => {
      const empRange = !!employmentId
        ? getEmploymentDateRange(+employmentId)
        : {
            startDate: undefined,
            endDate: undefined,
          };

      const sortRangeDates = (dates: (Date | string | null | undefined)[]) =>
        sortDates(dates.filter((d) => !!d).map((d) => moment(d).toDate()));

      return {
        startDateRange: {
          minDate: getLastInArray(
            sortRangeDates([empRange.startDate, startDate?.minDate]),
          ),
          maxDate: getFirstInArray(
            sortRangeDates([
              empRange.endDate,
              endDate?.value,
              endDate?.maxDate,
            ]),
          ),
        },
        endDateRange: {
          minDate: getLastInArray(
            sortRangeDates([
              empRange.startDate,
              startDate?.value,
              startDate?.minDate,
            ]),
          ),
          maxDate: getFirstInArray(
            sortRangeDates([empRange.endDate, endDate?.maxDate]),
          ),
        },
      };
    },
    [getEmploymentDateRange],
  );

  const { startDateRange, endDateRange } = useMemo(
    () => resolveDateFieldLimits(employmentId, startDate, endDate),
    [resolveDateFieldLimits, employmentId, startDate, endDate],
  );

  return {
    datePickers: {
      startDate: startDateRange,
      endDate: endDateRange,
    },
    isLoading,
  };
};
